import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from '@/lib/router-compat';
import { Menu, Search, LogOut, ChevronDown, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { destinationsFor, type NavAudience } from '@/lib/navigation';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { CommandPalette } from './CommandPalette';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import mavenWerxLogo from '@/assets/maven-werx-logo.png';

export function AppNav() {
  const { user, signOut } = useAuth();
  const { isStaff, isViewer, isFounder, loading } = useUserRole();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user || loading) return null;

  const audience: NavAudience | null = isStaff ? 'staff' : isViewer ? 'viewer' : isFounder ? 'founder' : null;
  if (!audience) return null;

  const destinations = destinationsFor(audience);
  const primary = destinations.filter((d) => d.primary);
  const secondary = destinations.filter((d) => !d.primary);
  const secondaryGroups = Array.from(new Set(secondary.map((d) => d.group)));

  const isActive = (to: string) => pathname === to || (to !== '/' && pathname.startsWith(`${to}/`));

  return (
    <>
      <header className="sticky top-0 z-40 bg-primary/95 backdrop-blur supports-[backdrop-filter]:bg-primary/80 border-b border-primary-foreground/10">
        <div className="flex h-14 items-center gap-2 px-4 md:px-6">
          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open navigation menu"
                className="md:hidden min-h-11 min-w-11 text-primary-foreground hover:bg-primary-foreground/10"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Go to</SheetTitle>
              </SheetHeader>
              <nav className="mt-4 space-y-1">
                {destinations.map((d) => (
                  <Link
                    key={d.to}
                    to={d.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-start gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                      isActive(d.to) ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                    }`}
                  >
                    <d.icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <span className="flex flex-col">
                      <span className="font-medium">{d.label}</span>
                      <span className="text-xs text-muted-foreground">{d.description}</span>
                    </span>
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          <SidebarTrigger
            aria-label="Toggle sidebar"
            className="hidden md:inline-flex text-primary-foreground hover:bg-primary-foreground/10"
          />

          <Link to="/dashboard" className="flex items-center gap-2 shrink-0" aria-label="MavenWERX home">
            <img src={mavenWerxLogo} alt="MavenWERX" className="h-7 w-auto brightness-125 [filter:drop-shadow(0_0_1px_hsl(0_0%_100%))_drop-shadow(0_0_1px_hsl(0_0%_100%))_drop-shadow(0_0_6px_hsl(0_0%_100%/0.7))]" />
          </Link>

          {/* Primary links */}
          <nav aria-label="Main" className="hidden md:flex min-w-0 flex-1 items-center gap-1 ml-2 overflow-x-auto [scrollbar-width:none]">
            {primary.map((d) => (
              <NavLink
                key={d.to}
                to={d.to}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                  isActive(d.to)
                    ? 'bg-primary-foreground/15 text-primary-foreground font-medium'
                    : 'text-primary-foreground/75 hover:bg-primary-foreground/10 hover:text-primary-foreground'
                }`}
              >
                <d.icon className="h-4 w-4" aria-hidden="true" />
                {d.label}
              </NavLink>
            ))}

            {secondary.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-8 px-3 text-sm text-primary-foreground/75 hover:bg-primary-foreground/10 hover:text-primary-foreground"
                  >
                    More
                    <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72 max-h-[70vh] overflow-y-auto">
                  {secondaryGroups.map((group, i) => (
                    <div key={group}>
                      {i > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                        {group}
                      </DropdownMenuLabel>
                      {secondary
                        .filter((d) => d.group === group)
                        .map((d) => (
                          <DropdownMenuItem key={d.to} onSelect={() => navigate(d.to)} className="items-start gap-2">
                            <d.icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                            <span className="flex flex-col">
                              <span>{d.label}</span>
                              <span className="text-xs text-muted-foreground">{d.description}</span>
                            </span>
                          </DropdownMenuItem>
                        ))}
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              onClick={() => setPaletteOpen(true)}
              className="hidden sm:flex h-9 items-center gap-2 rounded-md border border-primary-foreground/20 px-3 text-sm text-primary-foreground/75 hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
              <span>Search pages</span>
              <kbd className="ml-2 hidden lg:inline rounded border border-primary-foreground/25 px-1.5 py-0.5 text-[10px]">
                ⌘K
              </kbd>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Search pages"
              onClick={() => setPaletteOpen(true)}
              className="sm:hidden min-h-11 min-w-11 text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Search className="h-5 w-5" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-9 max-w-[180px] gap-2 px-2 text-sm text-primary-foreground/85 hover:bg-primary-foreground/10 hover:text-primary-foreground"
                >
                  <span className="truncate">{user.email}</span>
                  <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  Signed in as
                  <div className="truncate text-foreground">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate('/dashboard')}>Home</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setPaletteOpen(true)}>Search pages (⌘K)</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => signOut()} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Breadcrumbs + back, so you always know where you are */}
        <div className="flex items-center gap-1 border-t border-primary-foreground/10 bg-primary/60">
          {pathname !== '/dashboard' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="ml-3 h-7 gap-1 px-2 text-xs text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <ArrowLeft className="h-3 w-3" aria-hidden="true" />
              Back
            </Button>
          )}
          <AppBreadcrumbs />
        </div>
      </header>

      <CommandPalette audience={audience} open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}
